import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  QueueListIcon,
  RocketLaunchIcon,
  ScaleIcon,
  SignalIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline'
import { useAutonomySnapshot } from './hooks/useAutonomySnapshot.ts'
import { InfoCard, SkeletonText } from './ui-kit/index.js'

// Each card's icon is chosen for instant pattern recognition — the
// same outline-set we use across detail-pane cards. Icons are 11px,
// tertiary-text colored, opacity 0.85 (per .studio-info-card-label-icon
// in studio.css). The kill-switch card uses ArrowPathIcon (the loop)
// to signal "is the autonomy cycle running?".
const ICON_SIZE = 11
const iconProps = { width: ICON_SIZE, height: ICON_SIZE, 'aria-hidden': true }

// AutonomyCommandStrip (Wave 24, refactored Wave 28) — the missing
// dashboard surface that shows what the autonomy loop is doing at a
// glance. Composes useAutonomySnapshot into six InfoCards in an
// auto-fit grid, plus a full-span incidents banner when present.

function formatAge(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—'
  const minutes = Math.floor(ms / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function severityTone(severity) {
  if (severity === 'critical' || severity === 'error') return '#f06464'
  if (severity === 'warning') return '#d4582d'
  return '#88827a'
}

// snapshot can be passed in (preferred — DashboardView lifts the hook
// so the strip + footer share a single fetch). When omitted, the strip
// falls back to its own hook call so it stays usable standalone (tests,
// Storybook, future surfaces). authorityTopicCandidates/posts are
// retained as the hook's option signature for the standalone path.
export function AutonomyCommandStrip({
  snapshot: snapshotProp,
  authorityTopicCandidates = [],
  posts = [],
  onOpenTab,
}) {
  const ownSnapshot = useAutonomySnapshot({
    authorityTopicCandidates,
    posts,
    skipAutoFetch: Boolean(snapshotProp),
  })
  const snapshot = snapshotProp || ownSnapshot

  const {
    loading,
    state,
    stateError,
    incidents,
    cooldown,
    decisionTone,
    decisionsError,
    pillarLifecycle,
    pillarLifecycleError,
    topicQueue,
    todaysAutonomousPublishCount,
  } = snapshot

  const killSwitchEngaged = Boolean(state?.killSwitchEngaged)
  const openIncidents = (incidents || []).filter((i) => !i.resolvedAt)
  const lifecycleAlertCount =
    (pillarLifecycle?.candidates?.length || 0) +
    (pillarLifecycle?.pendingProposals?.length || 0)

  const sectionStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12,
    marginBottom: 24,
    padding: 0,
  }

  return (
    <section className="studio-autonomy-strip" style={sectionStyle}>
      {/* Card 1: Kill switch / autonomy state */}
      <InfoCard
        label="Autonomy state"
        icon={<ArrowPathIcon {...iconProps} />}
        tone={killSwitchEngaged ? 'error' : 'success'}
        onClick={() => onOpenTab && onOpenTab('autonomy')}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          {loading ? (
            <SkeletonText
              width={70}
              height={20}
              marginBottom={0}
              ariaLabel="Loading kill-switch status"
            />
          ) : stateError ? (
            'Unknown'
          ) : killSwitchEngaged ? (
            'Paused'
          ) : (
            'Active'
          )}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          {killSwitchEngaged && state?.killSwitchReason
            ? state.killSwitchReason.slice(0, 80)
            : stateError
              ? 'state fetch failed'
              : 'kill switch disengaged'}
        </div>
      </InfoCard>

      {/* Card 2: Today's autonomous publishes */}
      <InfoCard
        label="Auto today"
        icon={<RocketLaunchIcon {...iconProps} />}
        onClick={() => onOpenTab && onOpenTab('delivered')}
      >
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {todaysAutonomousPublishCount}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          autonomous publishes
        </div>
      </InfoCard>

      {/* Card 3: Decision tone (last 24h) */}
      <InfoCard
        label="Decisions · 24h"
        icon={<ScaleIcon {...iconProps} />}
        onClick={() => onOpenTab && onOpenTab('autonomy')}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {decisionsError ? (
            <span style={{ opacity: 0.5 }}>fetch failed</span>
          ) : (
            <>
              <span>{decisionTone.autoPublished} auto</span>
              <span style={{ opacity: 0.4 }}> · </span>
              <span>{decisionTone.blocked} blocked</span>
              <span style={{ opacity: 0.4 }}> · </span>
              <span>{decisionTone.assistPending} assist</span>
            </>
          )}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          {decisionTone.total} total · {decisionTone.manualOnly} manual
        </div>
      </InfoCard>

      {/* Card 4: Topic queue health */}
      <InfoCard
        label="Topic queue"
        icon={<QueueListIcon {...iconProps} />}
        onClick={() => onOpenTab && onOpenTab('ideas')}
      >
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {topicQueue.pendingCount}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          {topicQueue.autoDiscoveredPercent}% auto-discovered ·{' '}
          {topicQueue.oldestPendingAgeMs != null
            ? `oldest ${formatAge(topicQueue.oldestPendingAgeMs)}`
            : 'none pending'}
        </div>
      </InfoCard>

      {/* Card 5: Pillar lifecycle alerts */}
      <InfoCard
        label="Pillar lifecycle"
        icon={<Square3Stack3DIcon {...iconProps} />}
        tone={lifecycleAlertCount > 0 ? 'warning' : 'neutral'}
        onClick={() => onOpenTab && onOpenTab('autonomy')}
      >
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {pillarLifecycleError ? '—' : lifecycleAlertCount}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          {pillarLifecycleError
            ? 'fetch failed'
            : pillarLifecycle
              ? `${pillarLifecycle.candidates?.length || 0} deprecate · ${pillarLifecycle.pendingProposals?.length || 0} proposals`
              : 'Reviewing pillar lifecycle…'}
        </div>
      </InfoCard>

      {/* Card 6: Cooldown gauges */}
      <InfoCard label="Cooldown · 24h" icon={<SignalIcon {...iconProps} />}>
        {cooldown.length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.5 }}>idle</div>
        ) : (
          cooldown.slice(0, 3).map((row) => {
            const cap = Number(row.capPerDay) || 0
            const count = Number(row.count) || 0
            const pct = cap > 0 ? Math.min(100, (count / cap) * 100) : 0
            return (
              <div key={row.platform} style={{ marginBottom: 6 }}>
                <div
                  style={{
                    fontSize: 11,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>{row.platform}</span>
                  <span style={{ opacity: 0.7 }}>
                    {count}
                    {cap ? `/${cap}` : ''}
                  </span>
                </div>
                <div
                  style={{
                    height: 3,
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      background:
                        pct > 80
                          ? 'var(--studio-error)'
                          : 'rgba(255, 255, 255, 0.32)',
                    }}
                  />
                </div>
              </div>
            )
          })
        )}
      </InfoCard>

      {/* Open incidents banner — full-span when present */}
      {openIncidents.length > 0 && (
        <InfoCard
          label={`${openIncidents.length} unresolved incident${openIncidents.length === 1 ? '' : 's'}`}
          icon={<ExclamationTriangleIcon {...iconProps} />}
          tone="error"
          fullSpan
          onClick={() => onOpenTab && onOpenTab('autonomy')}
        >
          <div style={{ fontSize: 13 }}>
            {openIncidents.slice(0, 3).map((inc, idx) => (
              <span key={inc.id}>
                {idx > 0 ? ' · ' : ''}
                <span style={{ opacity: 0.6 }}>[{inc.severity}]</span>{' '}
                {inc.summary}
              </span>
            ))}
            {openIncidents.length > 3 ? (
              <span style={{ opacity: 0.7 }}>
                {' '}
                · +{openIncidents.length - 3} more
              </span>
            ) : null}
          </div>
        </InfoCard>
      )}
    </section>
  )
}
